import { SvgIcon } from "@material-ui/core";
import {
  ArrowBack,
  Check,
  Close,
  FolderOutlined,
  InsertDriveFileOutlined,
  Refresh,
  Sync,
} from "@material-ui/icons";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  CustomTable,
  ModalError,
  SearchTextInput,
  SecondaryButton,
  SectionLabel,
} from "components";
import { useCallback, useEffect, useRef, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import Swal from "sweetalert2";
import { getErrors, retryDownload } from "utils";

const TABLE_MAX_HEIGHT = 460;
const FolderUpIcon = props => (
  <SvgIcon {...props}>
    <path d="M20 6H12L10 4H4C2.89 4 2 4.89 2 6V18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V8C22 6.89 21.11 6 20 6ZM20 18H4V8H20V18ZM13 13V17H11V13H8L12 9L16 13H13Z" />
  </SvgIcon>
);
const CircularProgressWithLabel = ({ value, ...props }) => {
  const classes = globalStyles();
  const numericValue =
    typeof value === "number" ? value : parseFloat(value || 0);
  const displayLabel =
    numericValue < 10 && numericValue > 0 && !Number.isInteger(numericValue)
      ? `${numericValue.toFixed(1)}%`
      : `${Math.round(numericValue)}%`;

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      ml={1}
    >
      <CircularProgress
        variant="determinate"
        size={24}
        value={numericValue}
        {...props}
      />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
          className={classes.progressLabel}
        >
          {displayLabel}
        </Typography>
      </Box>
    </Box>
  );
};
const formatFileSize = sizeStr => {
  if (!sizeStr && sizeStr !== 0) return "";
  if (typeof sizeStr === "string" && /[a-zA-Z]/.test(sizeStr)) return sizeStr;
  const bytes = typeof sizeStr === "string" ? parseInt(sizeStr, 10) : sizeStr;
  if (isNaN(bytes) || bytes < 0) return String(sizeStr);
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${
    value < 10 && i > 0
      ? value.toFixed(2)
      : value < 100 && i > 0
      ? value.toFixed(1)
      : Math.round(value)
  } ${units[i]}`;
};

const ModalRemoteDirectory = ({ open, onClose, dsp }) => {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [lastSynced, setLastSynced] = useState(null);

  const [currentPath, setCurrentPath] = useState("");
  const [pathHistory, setPathHistory] = useState([]);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const dspSlug =
    dsp?.dsp_slug || dsp?.label?.toLowerCase()?.replace(/\s+/g, "-");

  const formatLastSynced = lastSynced => {
    if (!lastSynced) return "Not synced yet";
    const diffMs = Date.now() - lastSynced.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Last synced just now";
    if (diffMin < 60) return `Last synced ${diffMin} min ago`;
    const diffHours = (diffMin / 60).toFixed(1);
    return `Last synced ${diffHours} hours ago`;
  };
  const fetchFiles = useCallback(
    async (path = "", search = "", isBackground = false) => {
      if (!dspSlug) return;
      if (!isBackground) setIsLoading(true);
      try {
        const res = await axios.get(`${hardBaseUrl}/ccid-upload/list-remote`, {
          headers,
          params: { dsp_slug: dspSlug, path, search },
        });
        const payload = res?.data?.data || res?.data || res?.data?.files || {};
        const filesArray = Array.isArray(payload)
          ? payload
          : payload.files || [];
        setFiles(filesArray);
        if (res?.data?.data?.last_sync) {
          setLastSynced(new Date(res?.data?.data?.last_sync));
        }
      } catch (error) {
        if (!isBackground)
          ModalError(error?.response ? getErrors(error?.response) : error);
      } finally {
        if (!isBackground) setIsLoading(false);
      }
    },
    [dspSlug]
  );

  useEffect(() => {
    if (dspSlug) {
      setCurrentPath("");
      setPathHistory([]);
      setSearchQuery("");
      fetchFiles("", "");
    }
  }, [dspSlug, fetchFiles]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const handler = setTimeout(() => {
      if (dspSlug) {
        fetchFiles(currentPath, searchQuery);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Refresh when download completes in SFTPExplorer
  useEffect(() => {
    if (dspSlug) {
      fetchFiles(currentPath, "", true);
    }
  }, [dspSlug, currentPath, fetchFiles]);

  // Auto-refresh progress silently every 3 seconds if any item has active progress
  useEffect(() => {
    const hasActiveProgress = files.some(
      f =>
        f.progress_flag === true ||
        f.progress_flag === 1 ||
        f.progress_flag === "true" ||
        f.download_status === "R"
    );
    if (!hasActiveProgress || !dspSlug) return;

    const interval = setInterval(() => {
      fetchFiles(currentPath, "", true);
    }, 5000);

    return () => clearInterval(interval);
  }, [files, dspSlug, currentPath, fetchFiles]);

  // ─── Navigation ─────────────────────────────────────────────────────────────
  const handleNavigateInto = item => {
    if (!item.is_dir && item.type !== "folder" && item.type !== "directory")
      return;
    const newPath =
      item.path ||
      (currentPath === "" ? item.name : `${currentPath}/${item.name}`);
    setPathHistory(prev => [...prev, currentPath]);
    setCurrentPath(newPath);
    setSearchQuery("");
    fetchFiles(newPath, "");
  };

  const handleNavigateBack = () => {
    const prev = pathHistory[pathHistory.length - 1] ?? "";
    setPathHistory(h => h.slice(0, -1));
    setCurrentPath(prev);
    setSearchQuery("");
    fetchFiles(prev, "");
  };

  // ─── Sync ───────────────────────────────────────────────────────────────────
  const handleRefresh = async () => {
    try {
      await axios.post(
        `${hardBaseUrl}/ccid-upload/sync`,
        {},
        { headers, params: { dsp_slug: dspSlug } }
      );
      setLastSynced(new Date());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      fetchFiles(currentPath, searchQuery);
    }
  };

  const handleRetryItem = async item => {
    try {
      await retryDownload({
        process_id: item.process_id || item.id,
        dsp_slug: dspSlug,
        path: item.path || item.name,
      });
      Swal.fire({
        icon: "info",
        title: "Retry Started",
        text: `Retrying download for ${item.name}…`,
        timer: 3000,
        showConfirmButton: false,
      });
      fetchFiles(currentPath, "", true);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };

  const columnTable = [
    {
      name: "all",
      title: (
        <span className={classes.pathText}>
          /data/mpis/dsp/{dspSlug}/{currentPath === "" ? "" : currentPath}
        </span>
      ),
      renderText: item => {
        if (!item || typeof item !== "object") return null;
        if (item.is_back_button) {
          return (
            <Box
              display="flex"
              alignItems="center"
              className={classes.folderClickable}
              onClick={() => handleNavigateBack()}
            >
              <FolderUpIcon className={classes.folderIcon} />
              ...
            </Box>
          );
        }
        const isFolder =
          item.is_dir || item.type === "folder" || item.type === "directory";
        const status = item.progress_flag
          ? item.download_status
          : item.download_status ||
            (item.status === "success"
              ? "F"
              : item.status === "failed"
              ? "E"
              : null);

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box
              display="flex"
              alignItems="center"
              className={isFolder ? classes.folderClickable : ""}
              onClick={() => isFolder && handleNavigateInto(item)}
            >
              {isFolder ? (
                <FolderOutlined className={classes.folderIcon} />
              ) : (
                <InsertDriveFileOutlined className={classes.fileIcon} />
              )}
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="center">
                  {item.name}
                  {/* File size */}
                  {item.size != null && (
                    <Typography component="span" className={classes.sizeText}>
                      {" "}
                      ({formatFileSize(item.size)})
                    </Typography>
                  )}
                </Box>
                {/* Download Speed */}
                {status === "R" && item.download_speed && (
                  <Typography
                    variant="caption"
                    component="div"
                    className={classes.speedText}
                  >
                    {item.download_speed}
                  </Typography>
                )}
              </Box>

              {status === "R" && (
                <Box display="flex" alignItems="center" ml={2}>
                  <CircularProgressWithLabel value={item.download_progress} />
                </Box>
              )}
              {status === "Q" && (
                <Chip
                  label="Queue"
                  size="small"
                  variant="outlined"
                  color="info"
                  className={classes.queueChip}
                />
              )}

              {status === "E" && (
                <Tooltip title="Retry Download">
                  <IconButton
                    size="small"
                    className={classes.retryButton}
                    onClick={e => {
                      e.stopPropagation();
                      handleRetryItem(item);
                    }}
                  >
                    <Sync fontSize="small" className={classes.syncIcon} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {item?.progress_flag && (
              <Box display="flex" alignItems="center" gap={0.5}>
                {status === "F" && (
                  <>
                    <Typography
                      variant="body2"
                      fontStyle="italic"
                      className={classes.successText}
                    >
                      Download Success
                    </Typography>
                    <Check className={classes.successIcon} />
                  </>
                )}

                {status === "E" && (
                  <>
                    <Typography
                      variant="body2"
                      fontStyle="italic"
                      className={classes.failedText}
                    >
                      Download Failed
                    </Typography>
                    <Close className={classes.failedIcon} />
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      },
    },
  ];
  const allData =
    pathHistory.length > 0
      ? [{ is_back_button: true, name: "...", is_dir: true }, ...files]
      : files;

  const filteredData = searchQuery
    ? allData.filter(
        item =>
          item.is_back_button ||
          item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allData;
    
  return (
    <Dialog
      fullWidth={true}
      maxWidth="lg"
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Grid container columnGap={1}>
              <Grid item>
                <IconButton onClick={onClose}>
                  <ArrowBack />
                </IconButton>
              </Grid>
              <Grid item>
                <Typography fontSize={24} fontWeight={700}>
                  SFTP DSP
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container columnGap={1} alignItems="center">
              <Grid item>
                <SectionLabel subTitle={formatLastSynced(lastSynced)} />
              </Grid>
              <Grid item>
                <SecondaryButton
                  startIcon={<Refresh />}
                  label="Sync"
                  onClick={handleRefresh}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <SearchTextInput
          placeholder="Search files..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          clear={true}
          onClear={() => setSearchQuery("")}
          size="small"
          width="100%"
        />
        <CustomTable
          mt={2}
          isLoading={isLoading}
          columnTable={columnTable}
          data={filteredData}
          maxHeight={TABLE_MAX_HEIGHT}
          isPagination={false}
        />
      </DialogContent>
    </Dialog>
  );
};
export default ModalRemoteDirectory;
